import { useMemo, useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Pencil } from "lucide-react";
import { useI18n } from "@/i18n/i18n";

/**
 * TimeSelect — picks a time in "HH:MM" format.
 *
 * Shows a sorted list of the 96 most-used times of the day (every 15 min:
 * :00, :15, :30, :45). If the user needs an unusual minute (like 4:14),
 * they can toggle a text-input fallback via the pencil icon.
 *
 * Value is always stored as "HH:MM" 24h string — same format as native
 * <input type="time"> — so backwards compatibility is preserved.
 */
const QUARTER_HOUR_STEPS = (() => {
  const out = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 15, 30, 45]) {
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      out.push(`${hh}:${mm}`);
    }
  }
  return out;
})();

function formatDisplay(hhmm) {
  if (!hhmm) return "";
  const [hStr, mStr] = hhmm.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const period = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

function isQuickTime(hhmm) {
  if (!hhmm) return true;
  const m = parseInt(hhmm.split(":")[1] || "0", 10);
  return m === 0 || m === 15 || m === 30 || m === 45;
}

export default function TimeSelect({ value, onChange, placeholder, testId }) {
  const { t } = useI18n();
  const [customMode, setCustomMode] = useState(false);

  // If the incoming value isn't one of our quick options → auto-switch to
  // custom mode so the user sees their unusual time preserved instead of
  // silently reverting to the closest quick option.
  useEffect(() => {
    if (value && !isQuickTime(value)) {
      setCustomMode(true);
    }
  }, [value]);

  const options = useMemo(() => QUARTER_HOUR_STEPS.map((v) => ({ value: v, label: formatDisplay(v) })), []);

  if (customMode) {
    return (
      <div className="flex gap-1.5 items-center">
        <Input
          type="time"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId ? `${testId}-custom` : undefined}
          className="flex-1"
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setCustomMode(false)}
          title={t("time_select_use_quick")}
          className="shrink-0"
          data-testid={testId ? `${testId}-quick-toggle` : undefined}
        >
          {t("time_select_quick")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex gap-1.5 items-center">
      <Select value={value || ""} onValueChange={(v) => onChange(v)}>
        <SelectTrigger data-testid={testId} className="flex-1">
          <SelectValue placeholder={placeholder || "--:--"} />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => setCustomMode(true)}
        title={t("time_select_use_custom")}
        className="shrink-0"
        data-testid={testId ? `${testId}-custom-toggle` : undefined}
      >
        <Pencil className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
